import
  React, {
  useEffect
} from 'react';

import {
  useHistory
} from 'react-router-dom';

import {
  Environment
} from '@vodacom/web-env';

import {
  VFillContainer
} from '@vodacom/web-common';

import {
  BackButton
} from 'components';

import {
  CacheKeys
} from '../../../constants';

import {
  DrupalProvider
} from 'providers';

import {
  WeatherProvider
} from '../providers';

import {
  CitySelector,
  WeatherChart,
  WeatherFooter,
  WeatherWidgetDetail
} from '../components';

import {
  HandleRender,
  withScreenViewTagging
} from 'hocs';

import {
  Weather,
  getStorageWithExpiry,
  isEqual,
  setStorageWithExpiry
} from 'helpers';

const WeatherDetailUI = () => {

  const history = useHistory();

  const mountedRef = React.useRef(true);

  const [
    state,
    setState
  ] = React.useState({
    forecast: [],
    error: false,
    loading: true,
    isChangeCityOpen: false,
    city: getStorageWithExpiry('Location', 'city', Environment.current.cities?.[3].name),
    latitude: getStorageWithExpiry('Location', 'latitude', Environment.current.cities?.[3].latitude),
    longitude: getStorageWithExpiry('Location', 'longitude', Environment.current.cities?.[3].longitude),
    cities: Environment.current.cities
  });

  useEffect(() => {

    mountedRef.current = true;

    const init = async () => {

      await getWeatherForecast(
        state.city,
        state.latitude,
        state.longitude
      );

      await getCities();
    };

    init();

    return () => {
      mountedRef.current = false;
    };

  }, []);

  const getWeather = async (city, lat, lon) => {

    setState(prevState => ({
      ...prevState,
      loading: true,
      error: false
    }));

    const cacheKey = `weather--${city || `${lat},${lon}`}`;

    const weather = await WeatherProvider.getCachedItem(
      cacheKey,
      null,
      () => { return WeatherProvider.getWeather(city, lat, lon); }
    );

    if (!weather?.ok) {

      setState(prevState => ({
        ...prevState,
        loading: false,
        error: false
      }));

      return;
    }

    setStorageWithExpiry('Location', 'city', weather.result?.city);
    setStorageWithExpiry('Location', 'latitude', weather.result?.coord?.lat);
    setStorageWithExpiry('Location', 'longitude', weather.result?.coord?.lon);

    setState(prevState => ({
      ...prevState,
      city: weather.result?.city,
      latitude: weather.result?.coord?.lat,
      longitude: weather.result?.coord?.lon
    }));
  };

  const getWeatherForecast = async (city, lat, lon) => {

    await getWeather(city, lat, lon);

    const cacheKey = `weatherForecast--${city || `${lat},${lon}`}`;

    let weatherObjects = [];

    const forecast = await WeatherProvider.getCachedItem(
      cacheKey,
      null,
      () => { return WeatherProvider.getWeatherForecast(city, lat, lon); }
    );

    if (forecast?.ok && Array.isArray(forecast.result?.daily)) {

      let today = forecast.result?.current;
      let forecasts = forecast.result?.daily;

      weatherObjects.push(
        new Weather(today, city).mapToStandardCurrent()
      );

      forecasts.forEach(forecast => {

        weatherObjects.push(
          new Weather(forecast, city).mapToStandard()
        );
      });

      setState(prevState => ({
        ...prevState,
        city: forecast.result?.city,
        latitude: forecast.result?.lat || lat,
        longitude: forecast.result?.lon || lon,
        forecast: weatherObjects,
        loading: false
      }));

      return;
    }

    setState(prevState => ({
      ...prevState,
      loading: false,
      error: true
    }));

    return;
  };

  const geolocationError = async (error) => {

    let city = getStorageWithExpiry('Location', 'city', 'Johannesburg');
    let latitude = getStorageWithExpiry('Location', 'latitude', -26.2023);
    let longitude = getStorageWithExpiry('Location', 'longitude', 28.0436);

    setState(prevState => ({
      ...prevState,
      loading: true,
      isChangeCityOpen: false
    }));

    let response = await getWeather(city, latitude, longitude);
    await getWeatherForecast(response?.city, response?.latitude, response?.longitude);
  };

  const useLocation = async (position) => {

    setStorageWithExpiry('Location', 'city', null);
    setStorageWithExpiry('Location', 'latitude', position?.coords?.latitude);
    setStorageWithExpiry('Location', 'longitude', position?.coords?.longitude);

    setState(prevState => ({
      ...prevState,
      loading: true,
      isChangeCityOpen: false
    }));

    await getWeatherForecast(null, position?.coords?.latitude, position?.coords?.longitude);
  };

  const getLocation = async () => {

    if (navigator.geolocation) {

      navigator.geolocation.getCurrentPosition(
        useLocation,
        geolocationError,
        {
          timeout: Environment.current.locationTimeout
        }
      );
    }
  };

  const getCities = async () => {

    const cities = await DrupalProvider.getCachedItem(
      CacheKeys.Cities,
      null,
      DrupalProvider.getCities
    );

    if (cities?.ok && cities.result?.[0]?.length > 0) {

      setState(prevState => ({
        ...prevState,
        cities: cities.result?.[0]?.cities
      }));

      return;
    }
  };

  const onSelectedCityOk = async selectedCity => {

      setState(prevState => ({
        ...prevState,
        loading: true,
        isChangeCityOpen: false
      }));

      if (isEqual(selectedCity, 'My Current City')) {

        await getLocation();

        WeatherDetailUI.tagViewData = {
          ...WeatherDetailUI.tagViewData,
          weather_city: state.city
        };

        return;
      }
      else {

        setStorageWithExpiry('Location', 'city', selectedCity);

        const city = state.cities?.find(obj => obj.name === selectedCity);

        await getWeatherForecast(city?.name, city?.latitude, city?.longitude);

        WeatherDetailUI.tagViewData = {
          ...WeatherDetailUI.tagViewData,
          weather_city: state.city
        };
      }
  };

  const WeatherPage = () => {

    return (

      <VFillContainer>

        <HandleRender
          isError={state.error}
          isLoading={state.loading}>
          {
            state.isChangeCityOpen
              ? <CitySelector
                  selectedCity={state.cities?.find(obj => obj.name === state.city)?.name || 'My Current City'}
                  onCancelClick={() => {

                    setState(prevState => ({
                      ...prevState,
                      isChangeCityOpen: false
                    }));
                  }}
                  onOkClick={onSelectedCityOk}/>

              : <>
                  <BackButton
                    margin={'14px 0 14px 16px'}
                    onClick={() => {

                      history.goBack();
                    }} />

                  <WeatherWidgetDetail
                    city={state.city}
                    forecast={state.forecast}
                    setState={setState}/>

                  <WeatherChart
                    forecast={state.forecast}/>

                  <WeatherFooter
                    city={state.city}
                    cities={state.cities}/>
                </>
          }
        </HandleRender>
      </VFillContainer>
    );
  };

  return <WeatherPage/>;
};

WeatherDetailUI.pageName = 'cbu: connectu: detailed weather';

WeatherDetailUI.tagViewData = {
  page_section: 'detailed weather',
  weather_city: getStorageWithExpiry('Location', 'city')
};

export const WeatherDetail = withScreenViewTagging(WeatherDetailUI);